import {
  type AccountInfo,
  EventType,
  InteractionStatus,
  PublicClientApplication,
} from '@azure/msal-browser';
import { MsalProvider, useMsal } from '@azure/msal-react';
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import apiClient, { setMsalInstance } from '../services/apiClient';
import { fetchMyJobTitle } from '../services/graphService';
import { graphConfig, loginRequest } from './msalConfig';

export interface AuthUser {
  email: string;
  displayName: string;
  photoUrl: string | null;
  role: string;
  status: string;
  canAccessChat?: boolean;
  /** Primary key from the AppUser table — used by My Schedule to scope to current user. */
  appUserKey: string | null;
}

export interface AuthContextValue {
  user: AuthUser | null;
  role: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  accessToken: string | null;
  login: () => Promise<void> | void;
  logout: () => void;
  getAccessToken: () => Promise<string | null>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderInnerProps {
  children: React.ReactNode;
}

const fetchProfilePhoto = async (accessToken: string): Promise<string | null> => {
  try {
    const response = await fetch(graphConfig.photoEndpoint, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!response.ok) return null;
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
};

const AuthProviderInner = ({ children }: AuthProviderInnerProps) => {
  const { instance, accounts, inProgress } = useMsal();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const account: AccountInfo | null = accounts[0] ?? null;
  const isAuthenticated = !!account && !!user;

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!account) return null;
    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account,
      });
      setAccessToken(response.accessToken);
      return response.accessToken;
    } catch {
      try {
        const response = await instance.acquireTokenPopup(loginRequest);
        setAccessToken(response.accessToken);
        return response.accessToken;
      } catch {
        return null;
      }
    }
  }, [account, instance]);

  const login = useCallback(async () => {
    try {
      await instance.loginPopup(loginRequest);
    } catch {
      // User closed popup or error — silently ignore
    }
  }, [instance]);

  const logout = useCallback(() => {
    setUser(null);
    setAccessToken(null);
    instance.logoutPopup({
      postLogoutRedirectUri: window.location.origin,
      mainWindowRedirectUri: window.location.origin,
    });
  }, [instance]);

  // Initialize user after MSAL login
  useEffect(() => {
    const initUser = async () => {
      if (inProgress !== InteractionStatus.None || !account) {
        if (inProgress === InteractionStatus.None && !account) {
          setIsLoading(false);
        }
        return;
      }

      try {
        const token = await getAccessToken();
        if (!token) {
          setIsLoading(false);
          return;
        }

        // Fetch user role/status from backend
        let role = 'reader';
        let status = 'active';
        let canAccessChat = false;
        let appUserKey: string | null = null;
        let jobClassEmpty = false;
        try {
          const res = await apiClient.get('/users/me');
          role = res.data.Role ?? res.data.role ?? 'reader';
          status = res.data.Status ?? res.data.status ?? 'active';
          canAccessChat = !!res.data.CanAccessChat;
          // AppUserKey is the UUID primary key — used by My Schedule page.
          appUserKey = (res.data.AppUserKey as string | undefined) ?? null;
          jobClassEmpty = !(res.data.JobClass && String(res.data.JobClass).trim());
        } catch (err: unknown) {
          const axiosErr = err as { response?: { status?: number; data?: { error?: string } } };
          if (axiosErr.response?.status === 404 || axiosErr.response?.status === 403) {
            // 404 = new user, 403 = pending/disabled — auth middleware auto-creates as pending
            const errMsg = axiosErr.response?.data?.error || '';
            status = errMsg.toLowerCase().includes('disabled') ? 'disabled' : 'pending';
          }
        }

        // Fetch profile photo from Graph API
        const photoUrl = await fetchProfilePhoto(token);

        setUser({
          email: account.username,
          displayName: account.name ?? account.username,
          photoUrl,
          role,
          status,
          canAccessChat,
          appUserKey,
        });

        // Backfill Job Class from the GAL on login when it's empty. Fill-if-empty:
        // the backend never overwrites an existing value, so manual edits survive.
        if (jobClassEmpty && appUserKey && status === 'active') {
          fetchMyJobTitle(token)
            .then((jobTitle) => {
              if (jobTitle) {
                return apiClient.patch('/users/me/profile', { jobClass: jobTitle });
              }
            })
            .catch(() => { /* best-effort — ignore backfill failures */ });
        }
      } catch {
        // silently fail
      } finally {
        setIsLoading(false);
      }
    };

    initUser();
  }, [account, inProgress, getAccessToken]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      role: user?.role ?? null,
      isAuthenticated,
      isLoading,
      accessToken,
      login,
      logout,
      getAccessToken,
    }),
    [user, isAuthenticated, isLoading, accessToken, login, logout, getAccessToken],
  );

  // ── Dev-preview auth stub (DEV-PREVIEW.md) ────────────────────────────────
  // Active ONLY when running the Vite dev server with VITE_DEV_AUTH=1 in
  // frontend/.env.local. `import.meta.env.DEV` is statically false in
  // production builds, so this whole branch is dead-code-eliminated from the
  // deployed bundle — setting the env var cannot enable it in prod. The
  // matching backend bypass (authMiddleware DEV_USER) is likewise gated on
  // NODE_ENV=development, so the stub only works against a dev API.
  if (import.meta.env.DEV && import.meta.env.VITE_DEV_AUTH === '1') {
    return (
      <AuthContext.Provider
        value={{
          user: { email: 'dev@bgcengineering.ca', displayName: 'Dev User', photoUrl: null, role: 'admin', status: 'active', canAccessChat: false, appUserKey: null },
          role: 'admin', isAuthenticated: true, isLoading: false, accessToken: null,
          login: () => {}, logout: () => {}, getAccessToken: async () => null,
        }}
      >
        {children}
      </AuthContext.Provider>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

interface AuthProviderProps {
  children: React.ReactNode;
  msalInstance: PublicClientApplication;
}

export const AuthProvider = ({ children, msalInstance }: AuthProviderProps) => {
  // Share MSAL instance with apiClient SYNCHRONOUSLY — must happen before any child effects
  setMsalInstance(msalInstance);

  // Wait for MSAL initialization (MSAL v5+)
  useEffect(() => {
    const init = async () => {
      try { await (msalInstance as any).initialize?.(); } catch { /* already initialized */ }
      await msalInstance.handleRedirectPromise();
    };
    init().catch(console.error);

    // Set active account on login success
    const callbackId = msalInstance.addEventCallback((event) => {
      if (
        event.eventType === EventType.LOGIN_SUCCESS &&
        event.payload &&
        'account' in event.payload
      ) {
        const payload = event.payload as { account: AccountInfo };
        msalInstance.setActiveAccount(payload.account);
      }
    });

    return () => {
      if (callbackId) {
        msalInstance.removeEventCallback(callbackId);
      }
    };
  }, [msalInstance]);

  return (
    <MsalProvider instance={msalInstance}>
      <AuthProviderInner>{children}</AuthProviderInner>
    </MsalProvider>
  );
};
